import { ReactNode } from 'react';
import { TEXT } from './Typography';

/**
 * The look of a form field and its label. FormInput and FormTextarea use these;
 * a raw field that cannot use them (a date with its own handling, a field that
 * needs a ref) imports them rather than keeping a private copy that drifts.
 */
export const FIELD_LABEL_CLASS = 'block text-sm font-medium text-gray-700 mb-1';
export const FIELD_INPUT_CLASS =
  'block w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500';
/** The same field in its error state: the border is swapped, not stacked, so no class order decides it. */
export const FIELD_INPUT_ERROR_CLASS = FIELD_INPUT_CLASS.replace('border-gray-300', 'border-red-300');
/** A field inside a table row or a toolbar: the same look, tighter. Width is the caller's. */
export const FIELD_INPUT_COMPACT_CLASS =
  'rounded-md border border-gray-300 bg-white px-2 py-1 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500';
/** A checkbox. */
export const CHECKBOX_CLASS = 'h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500';

interface FormInputProps {
  label: string;
  /**
   * Explicit DOM id. Without one the id is derived from the label text, so N
   * rows sharing a label (ten "Amount" fields in a split) emit N elements with
   * the same id — invalid, and every label then focuses the first row.
   * Optional, so the four existing consumers are unaffected.
   */
  id?: string;
  value: string | number;
  onChange: (value: string) => void;
  onBlur?: () => void;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'date' | 'datetime-local' | 'time';
  inputMode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';
  placeholder?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  icon?: ReactNode;
  /** Right-hand control inside the field, e.g. a show/hide password button. */
  trailing?: ReactNode;
  /** Passed through, so password managers and autofill work (e.g. 'current-password'). */
  autoComplete?: string;
  min?: number;
  max?: number;
  step?: number;
  maxLength?: number;
}

export default function FormInput({
  label,
  id,
  value,
  onChange,
  onBlur,
  type = 'text',
  inputMode,
  placeholder,
  error,
  helperText,
  required = false,
  disabled = false,
  className = '',
  icon,
  trailing,
  autoComplete,
  min,
  max,
  step,
  maxLength,
}: FormInputProps) {
  const inputId = id || `input-${label.toLowerCase().replace(/\s+/g, '-')}`;
  // Error and helper text are announced with the field, not just shown near it.
  const noteId = error || helperText ? `${inputId}-note` : undefined;

  return (
    <div className={className}>
      <label htmlFor={inputId} className={FIELD_LABEL_CLASS}>
        {label}
        {required && <span className="text-red-600 ml-1">*</span>}
      </label>
      <div className="relative">
        {icon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="text-gray-500">{icon}</span>
          </div>
        )}
        <input
          id={inputId}
          type={type}
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          aria-invalid={error ? true : undefined}
          aria-describedby={noteId}
          min={min}
          max={max}
          step={step}
          maxLength={maxLength}
          className={`${error ? FIELD_INPUT_ERROR_CLASS : FIELD_INPUT_CLASS} ${icon ? 'pl-10' : ''} ${trailing ? 'pr-12' : ''}`}
        />
        {trailing && <div className="absolute inset-y-0 right-0 flex items-center pr-1">{trailing}</div>}
      </div>
      {error && <p id={noteId} className="mt-1 text-sm text-red-600">{error}</p>}
      {helperText && !error && <p id={noteId} className={`mt-1 ${TEXT.helper}`}>{helperText}</p>}
    </div>
  );
}
